import React from 'react';
import PropTypes from 'prop-types';

import {Box, Divider, Image, Paper, Typography} from 'views/components';
import {
  AssetPriorityFlagBlue as PriorityFlagBlue,
  AssetPriorityFlagGrey as PriorityFlagGrey,
  AssetPriorityFlagOrange as PriorityFlagOrange,
  AssetPriorityFlagRed as PriorityFlagRed,
} from 'views/icons';
import {Translate, translate, useNavigate} from 'views/module';

import moment from 'moment';

const JobCard = (props) => {
  const {item} = props;

  const job = item;
  const navigation = useNavigate();
  const priorityConfig = React.useMemo(() => {
    if (!job?.priority) {
      return null;
    }

    const mappedPriority = translate(`career.Priority.${job.priority}`);
    return mappedPriority;
  }, [job?.priority]);
  const priorityIcons = {
    PRIORITY_1: PriorityFlagRed,
    PRIORITY_2: PriorityFlagOrange,
    PRIORITY_3: PriorityFlagBlue,
    PRIORITY_4: PriorityFlagGrey,
  };
  const priorityIcon = priorityIcons[job?.priority] || null;

  const metrics = React.useMemo(() => {
    const hourlyRate = job.price?.hourlyRate || 0;
    let scheduleMetrics = {
      totalHours: 0,
      shiftCount: 0,
      weeklyPay: null,
      hasEstimate: false,
    };

    if (job.schedule?.type === 'LIVEIN_EVENT') {
      const totalHours = 56;
      scheduleMetrics = {
        totalHours,
        shiftCount: 7,
        weeklyPay: totalHours * hourlyRate,
        hasEstimate: true,
      };
    }

    if (job.schedule?.type === 'RECURRENCE_EVENT') {
      const weeklySlots = job.schedule.recurrenceRule?.weeklySlots || [];
      const totalHours = weeklySlots.reduce((total, slot) => {
        const start = moment(slot.startTime);
        const end = moment(slot.endTime);
        const hoursPerSlot = end.diff(start, 'hours', true);

        return total + Math.abs(hoursPerSlot);
      }, 0);

      scheduleMetrics = {
        totalHours,
        shiftCount: weeklySlots.length,
        weeklyPay: totalHours * hourlyRate,
        hasEstimate: true,
      };
    }

    if (job.schedule?.type === 'FLEXIBLE') {
      const flexibleSchedule = job.schedule.flexibleSchedule || {};
      const shiftCount = (flexibleSchedule.preferredDays || []).length;
      const durationText = `${flexibleSchedule.visitDuration ?? ''}`.trim();
      const parsedHours = Number.parseFloat(durationText.replace(/[^0-9.]/g, ''));
      const totalHours = (Number.isFinite(parsedHours) ? parsedHours : 0) * shiftCount;
      const hasEstimate = totalHours > 0 && shiftCount > 0;

      scheduleMetrics = {
        totalHours,
        shiftCount,
        weeklyPay: hasEstimate ? totalHours * hourlyRate : null,
        hasEstimate,
      };
    }

    let scheduleChips = [];

    if (job.schedule?.type === 'RECURRENCE_EVENT') {
      scheduleChips = (job.schedule.recurrenceRule?.weeklySlots || []).map((slot) => {
        const dayName = translate(`career.create.Schedule.Day.${slot.day}`);
        const startTime = moment(slot.startTime).format('h:mm A');
        const endTime = moment(slot.endTime).format('h:mm A');
        const isOvernight = moment(slot.endTime).isAfter(moment(slot.startTime), 'day');
        const overnightLabel = isOvernight ? ' (overnight)' : '';

        return `${dayName}${overnightLabel}: ${startTime} - ${endTime}`;
      });
    }

    if (job.schedule?.type === 'LIVEIN_EVENT') {
      scheduleChips = [translate('career.create.Schedule.ScheduleType.LIVEIN_EVENT')];
    }

    if (job.schedule?.type === 'FLEXIBLE') {
      const flexibleSchedule = job.schedule.flexibleSchedule || {};
      const preferredDays = (flexibleSchedule.preferredDays || [])
          .map((value) => translate(`career.create.Schedule.PreferredDayOptions.${value}`))
          .join(', ');
      const preferredTimes = (flexibleSchedule.preferredTimes || [])
          .map((value) => translate(`career.create.Schedule.PreferredTimeOptions.${value}`))
          .join(', ');
      scheduleChips = [translate('career.create.Schedule.ScheduleType.FLEXIBLE')];

      if (preferredDays) {
        scheduleChips.push(translate('career.create.Schedule.PreferredDaysDisplay', {value: preferredDays}));
      }

      if (preferredTimes) {
        scheduleChips.push(translate('career.create.Schedule.PreferredTimesDisplay', {value: preferredTimes}));
      }

      if ((flexibleSchedule.visitDuration || '').trim()) {
        scheduleChips.push(translate('career.create.Schedule.VisitDurationDisplay', {value: flexibleSchedule.visitDuration.trim()}));
      }
    }

    return {
      ...scheduleMetrics,
      scheduleChips,
    };
  }, [job]);

  const handleCardClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) {
      return;
    }

    if (event.metaKey || event.ctrlKey) {
      window.open(`/career/${job.jobRn}`, '_blank', 'noopener,noreferrer');
      return;
    }

    navigation(`/career/${job.jobRn}`);
  };

  const handleAuxClick = (event) => {
    if (event.defaultPrevented || event.button !== 1) {
      return;
    }

    event.preventDefault();
    window.open(`/career/${job.jobRn}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <Paper
      sx={{
        'overflow': 'hidden',
        'p': 2,
        'bgcolor': '#ffffff',
        'width': '100%',
        'opacity': 1,
        'border': 'none',
        'cursor': 'pointer',
        '&:hover': {
          backgroundColor: '#f9f9f9',
        },
      }}
      onClick={handleCardClick}
      onAuxClick={handleAuxClick}
    >
      {/* Header: Location and Weekly Pay */}
      <Box
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
        }}
      >
        <Box style={{display: 'flex', flexDirection: 'row', alignItems: 'center', flex: 1}}>
          <Typography variant="h5" component="div" style={{marginRight: 12}}>
            <Translate
              expression={'career.jobCard.Location'}
              substitute={{
                clientName: job?.client?.clientName || translate('career.jobCard.ClientUnknown'),
                neighborhood: job.location.neighborhood,
                city: job.location.city,
              }}
            />
          </Typography>

          {/* Applicants Chip */}
          <Box
            style={{
              backgroundColor: '#e3f2fd',
              borderColor: '#1976d2',
              borderWidth: 1,
              borderRadius: 16,
              alignItems: 'center',
              display: 'inline-flex',
              padding: '4px 8px',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            <Typography sx={{fontSize: 12, color: '#1976d2', fontWeight: 500, whiteSpace: 'nowrap'}}>
              <Translate
                expression={'career.jobCard.Applications'}
                substitute={{
                  count: job.applications.length,
                }}
              />
            </Typography>
          </Box>
          {
            priorityIcon &&
            <Image
              alt={priorityConfig?.Title || ''}
              src={priorityIcon}
              sx={{
                width: '14px',
                height: '14px',
                ml: '8px',
                flexShrink: 0,
              }}
            />
          }
        </Box>
      </Box>

      {/* Hourly Rate, Hours per Week, Shift Count */}
      <Typography sx={{fontSize: 16}} color="text.secondary">
        {
          metrics.hasEstimate &&
          <Translate
            expression={'career.jobCard.JobDetails'}
            substitute={{
              amount: metrics.weeklyPay.toFixed(2),
              rate: job.price.hourlyRate,
              hours: metrics.totalHours.toFixed(1),
              count: metrics.shiftCount,
            }}
          />
        }
        {
          !metrics.hasEstimate &&
          job.schedule.type === 'FLEXIBLE' &&
          <Translate expression={'career.jobCard.JobDetailsFlexible'} substitute={{rate: job.price.hourlyRate}} />
        }
      </Typography>

      <Divider />


      {/* Short Description */}
      <Typography sx={{fontSize: 12, marginBottom: 2}} color="text.secondary">
        {job.description.short}
      </Typography>

      {/* Schedule Chips */}
      <Box
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          justifyContent: 'center',
          marginBottom: 4,
          alignItems: 'flex-start',
        }}
      >
        {
          metrics.scheduleChips.map((chip, index) => (
            <Box
              key={index}
              style={{
                backgroundColor: '#f5f5f5',
                borderColor: '#e0e0e0',
                borderWidth: 1,
                borderRadius: 16,
                alignItems: 'center',
                marginRight: 12,
                display: 'inline-flex',
                padding: '4px 8px',
              }}
            >
              <Typography sx={{fontSize: 12}} color="text.secondary">
                {chip}
              </Typography>
            </Box>
          ))
        }
      </Box>
    </Paper>
  );
};

JobCard.propTypes = {
  item: PropTypes.shape({
    jobRn: PropTypes.string.isRequired,
    applications: PropTypes.array.isRequired,
    description: PropTypes.shape({
      short: PropTypes.string,
    }),
    location: PropTypes.shape({
      city: PropTypes.string,
      neighborhood: PropTypes.string,
    }),
    schedule: PropTypes.shape({
      type: PropTypes.oneOf(['RECURRENCE_EVENT', 'LIVEIN_EVENT', 'FLEXIBLE']).isRequired,
      recurrenceRule: PropTypes.shape({
        weeklySlots: PropTypes.arrayOf(PropTypes.shape({
          day: PropTypes.string,
          startTime: PropTypes.string,
          endTime: PropTypes.string,
        })),
        timezone: PropTypes.string,
      }),
      flexibleSchedule: PropTypes.shape({
        preferredDays: PropTypes.arrayOf(PropTypes.string),
        preferredTimes: PropTypes.arrayOf(PropTypes.string),
        visitDuration: PropTypes.string,
      }),
    }),
    price: PropTypes.shape({
      hourlyRate: PropTypes.number,
    }),
  }).isRequired,
  status: PropTypes.string,
};

export default JobCard;
